/**
 * VideoPreviewStep - Preview blurred video before download
 *
 * Shows a video player with the processed video (watermarked preview)
 * and displays blur statistics.
 */
import React, { useState, useEffect, useRef } from 'react';
import VideoProgressBar from './VideoProgressBar';
import { useVideoProgress, useVideoProcessing } from '../hooks/useVideoProgress';
import { videoAPI, formatDuration } from '../utils/videoUtils';
import { API_URL } from '../utils/constants';

// Helper to fix relative URLs
const fixUrl = (url) => url?.startsWith('/') ? `${API_URL}${url}` : url;

export default function VideoPreviewStep({
  file,
  fileIndex,
  onFileChange,
  onShowToast,
}) {
  const [processingComplete, setProcessingComplete] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const videoRef = useRef(null);

  const { jobId, isStarting, startProcessing } = useVideoProcessing();

  // Progress tracking
  const {
    progress,
    eta,
    step,
    status,
    error,
    result,
    isProcessing,
    isComplete,
    isFailed,
  } = useVideoProgress(jobId, {
    jobType: 'processing', // Use processing endpoint to get preview_url/download_url
    onComplete: (data) => {
      setProcessingComplete(true);
      const fixedPreviewUrl = fixUrl(data.preview_url);
      const fixedDownloadUrl = fixUrl(data.download_url);
      if (fixedPreviewUrl) {
        setPreviewUrl(fixedPreviewUrl);
      }
      onFileChange(prev => ({
        ...prev,
        previewUrl: fixedPreviewUrl,
        downloadUrl: fixedDownloadUrl,
        processed: true
      }));
      onShowToast?.('Video processing complete!', 'success');
    },
    onError: (msg) => {
      onShowToast?.(`Processing failed: ${msg}`, 'error');
    },
  });

  // Start processing on mount if not already done
  useEffect(() => {
    if (file?.sessionId && file?.fileId && file?.blurSettings && !file?.processed && !jobId && !processingComplete) {
      // Check if any blur is enabled
      const hasBlur = Object.values(file.blurSettings).some(s => s.enabled);
      if (hasBlur) {
        startProcessing(file.sessionId, file.fileId, file.blurSettings)
          .catch((err) => {
            onShowToast?.(`Failed to start processing: ${err.message}`, 'error');
          });
      } else {
        onShowToast?.('No persons selected for blur. Go back and enable blur for at least one person.', 'error');
      }
    } else if (file?.previewUrl) {
      setProcessingComplete(true);
      setPreviewUrl(fixUrl(file.previewUrl));
    }
  }, [file?.sessionId, file?.fileId, file?.blurSettings, file?.processed, file?.previewUrl, jobId, processingComplete, startProcessing, onShowToast]);

  // Also update file state when isComplete changes (backup for onComplete callback)
  useEffect(() => {
    if (isComplete && result && !file?.processed) {
      const fixedPreviewUrl = fixUrl(result.preview_url);
      const fixedDownloadUrl = fixUrl(result.download_url);
      setProcessingComplete(true);
      if (fixedPreviewUrl) {
        setPreviewUrl(fixedPreviewUrl);
      }
      onFileChange(prev => ({
        ...prev,
        previewUrl: fixedPreviewUrl,
        downloadUrl: fixedDownloadUrl,
        processed: true
      }));
    }
  }, [isComplete, result, file?.processed, onFileChange]);

  // Count blurred persons
  const blurredCount = Object.values(file?.blurSettings || {}).filter(s => s.enabled).length;

  // Calculate trimmed duration (if trimRange exists, use it; otherwise use full duration)
  const trimmedDuration = file?.trimRange
    ? (file.trimRange.end - file.trimRange.start)
    : file?.metadata?.duration_seconds;

  // Check if already complete (from file state or hook state)
  const isAlreadyComplete = file?.processed || processingComplete || isComplete;

  // Show progress bar during processing (but not if already complete)
  if ((isProcessing || isStarting) && !isAlreadyComplete) {
    return (
      <div className="max-w-2xl mx-auto px-4">
        {/* Video Info */}
        <div className="bg-gray-800/50 rounded-xl p-4 mb-6 border border-gray-700">
          <div className="flex items-center gap-4">
            {file?.thumbnail && (
              <img
                src={file.thumbnail}
                alt={file.name}
                className="w-24 h-16 object-cover rounded-lg"
              />
            )}
            <div>
              <h3 className="text-white font-medium">{file?.name}</h3>
              <p className="text-sm text-gray-400">
                Blurring {blurredCount} person(s)
              </p>
            </div>
          </div>
        </div>

        {/* Progress */}
        <VideoProgressBar
          progress={progress}
          eta={eta}
          step={step}
          status={status}
          title="Processing Video"
        />

        <p className="text-center text-gray-400 text-sm mt-4">
          This may take a few minutes depending on video length...
        </p>
      </div>
    );
  }

  // Show error state (but not if we already have a preview)
  if (isFailed && !previewUrl && !file?.previewUrl) {
    return (
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-red-900/20 rounded-xl p-6 border border-red-500/50 text-center">
          <svg className="w-12 h-12 text-red-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h3 className="text-xl font-medium text-red-400 mb-2">Processing Failed</h3>
          <p className="text-gray-400">{error}</p>
          <button
            onClick={() => startProcessing(file.sessionId, file.fileId, file.blurSettings)}
            className="mt-4 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
          >
            Retry Processing
          </button>
        </div>
      </div>
    );
  }

  // Get the preview URL from local state or file state
  const displayPreviewUrl = previewUrl || fixUrl(file?.previewUrl);

  // Preview UI
  return (
    <div className="max-w-4xl mx-auto px-4">
      {/* Video Player */}
      <div className="bg-gray-900 rounded-xl overflow-hidden mb-6">
        {displayPreviewUrl ? (
          <div className="relative">
            <video
              ref={videoRef}
              src={displayPreviewUrl}
              controls
              className="w-full max-h-[60vh] object-contain"
              poster={file?.thumbnail}
            >
              Your browser does not support video playback.
            </video>

            {/* Watermark notice */}
            <div className="absolute top-4 right-4 bg-yellow-500/80 text-yellow-900 text-xs px-2 py-1 rounded font-medium">
              PREVIEW - Watermarked
            </div>
          </div>
        ) : (
          <div className="aspect-video flex items-center justify-center bg-gray-800">
            <div className="text-center">
              <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-400">Video preview not available</p>
            </div>
          </div>
        )}
      </div>

      {/* Video Info */}
      <div className="bg-gray-800/50 rounded-xl p-4 mb-6 border border-gray-700">
        <h3 className="text-white font-medium mb-4">{file?.name}</h3>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="bg-gray-700/50 rounded-lg p-3">
            <p className="text-2xl font-bold text-emerald-400">{blurredCount}</p>
            <p className="text-xs text-gray-400">Persons Blurred</p>
          </div>
          <div className="bg-gray-700/50 rounded-lg p-3">
            <p className="text-2xl font-bold text-white">
              {formatDuration(trimmedDuration)}
            </p>
            <p className="text-xs text-gray-400">Duration</p>
          </div>
          <div className="bg-gray-700/50 rounded-lg p-3">
            <p className="text-2xl font-bold text-white">
              {file?.metadata?.width}x{file?.metadata?.height}
            </p>
            <p className="text-xs text-gray-400">Resolution</p>
          </div>
          <div className="bg-gray-700/50 rounded-lg p-3">
            <p className="text-2xl font-bold text-white">
              {file?.metadata?.fps?.toFixed(0)}
            </p>
            <p className="text-xs text-gray-400">FPS</p>
          </div>
        </div>
      </div>

      {/* Blur Settings Summary */}
      {blurredCount > 0 && (
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <h4 className="text-white font-medium mb-3">Blur Settings Applied</h4>
          <div className="space-y-2">
            {Object.entries(file?.blurSettings || {})
              .filter(([_, s]) => s.enabled)
              .map(([personId, settings]) => {
                const person = file?.detections?.find(p => p.person_id === personId);
                return (
                  <div
                    key={personId}
                    className="flex items-center justify-between bg-gray-700/50 rounded-lg px-3 py-2"
                  >
                    <div className="flex items-center gap-2">
                      {person?.thumbnail_url && (
                        <img
                          src={person.thumbnail_url}
                          alt={personId}
                          className="w-8 h-8 rounded object-cover"
                        />
                      )}
                      <span className="text-gray-300 capitalize">
                        {personId.replace('_', ' ')}
                      </span>
                      {person?.gender && (
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          person.gender === 'male' ? 'bg-blue-500/50 text-blue-200' : 'bg-pink-500/50 text-pink-200'
                        }`}>
                          {person.gender}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-400">{settings.level}</span>
                      <span className="text-gray-500">•</span>
                      <span className="text-gray-400">{settings.target}</span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Notice */}
      <div className="mt-6 bg-gray-800/50 rounded-xl p-4 border border-gray-700">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="text-gray-300 text-sm">
              This preview includes a watermark. The final download will be watermark-free.
            </p>
            <p className="text-gray-500 text-xs mt-1">
              Click "Continue" to proceed to download.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
